import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const envUrl = process.env.REACT_APP_BACKEND_URL || '';
const isProduction = typeof window !== 'undefined' && !window.location.hostname.includes('preview.emergentagent.com') && window.location.hostname !== 'localhost';
const API_URL = isProduction ? window.location.origin : (envUrl || window.location.origin);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState<Record<string, unknown> | null>(null); // 'password', 'sso', 'denied', or null
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for error messages in URL (from SSO callback)
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    if (error) {
      const errorMessages = {
        'domain_not_allowed': 'Your email domain is not authorized for this application.',
        'account_inactive': 'Your account has been deactivated. Please contact an administrator.',
        'invalid_state': 'Login session expired. Please try again.',
        'azure_api_error': 'Microsoft authentication failed. Please try again.',
        'sso_error': message || 'Single sign-on failed. Please try again.'
      };
      setErrorMessage(errorMessages[error] || message || 'Login failed. Please try again.');
    }
  }, [searchParams]);

  // Check email authentication method when email changes
  useEffect(() => {
    const checkEmailMethod = async () => {
      if (!email || !email.includes('@')) {
        setAuthMethod(null);
        return;
      }

      setCheckingEmail(true);
      try {
        const response = await axios.get(`${API_URL}/api/auth/sso/check-email`, {
          params: { email }
        });
        setAuthMethod(response.data.method);
        if (response.data.method === 'denied') {
          setErrorMessage('Your email domain is not authorized for this application. Please contact an administrator.');
        } else {
          setErrorMessage('');
        }
      } catch (error: unknown) {
        console.error('Error checking email:', error);
        setAuthMethod('password'); // Fallback to password
      } finally {
        setCheckingEmail(false);
      }
    };

    const debounceTimer = setTimeout(checkEmailMethod, 500);
    return () => clearTimeout(debounceTimer);
  }, [email]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await login(email, password);
      navigate('/');
    } catch (error: unknown) {
      console.error('Login failed:', error);
      setErrorMessage(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.get(`${API_URL}/api/auth/azure/login`, {
        params: { redirect_url: '/' }
      });
      // Redirect to Azure login
      window.location.href = response.data.auth_url;
    } catch (error: unknown) {
      console.error('SSO initiation failed:', error);
      setErrorMessage('Failed to initiate Microsoft sign-in. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (authMethod === 'sso') {
      handleSSOLogin();
    } else {
      handlePasswordLogin(e);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1684278313482-7dc138f4b785?auto=format&fit=crop&w=1920&q=80')` }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60" />

      <div className="w-full max-w-md relative z-10">
        <div className="alliance-card p-8 backdrop-blur-sm bg-wmt-primary shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_101bb3ff-a91b-44b5-9655-2a8dbd26c2f1/artifacts/dknvzqxd_alliance-branding-min-red.png" 
              alt="Alliance Logo" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-wmt-primary" >              Work Management Tracker
            </h1>
            <p className="text-sm mt-2 text-wmt-secondary" >              Sign in to your account
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 p-3 rounded border-2 bg-status-error border-[var(--error-text)]">
              <p className="text-sm font-medium text-status-error">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-wmt-primary" >                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="alliance-input"
                placeholder="you@alliance.us.com"
                required
                data-testid="email-input"
              />
              {checkingEmail && (
                <p className="text-xs mt-1 text-wmt-secondary" >                  Checking authentication method...
                </p>
              )}
            </div>

            {/* Show password field only for password auth users */}
            {authMethod === 'password' && (
              <div>
                <label className="block text-sm font-medium mb-1 text-wmt-primary" >                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="alliance-input"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            )}

            {/* SSO user message */}
            {authMethod === 'sso' && (
              <div className="p-3 rounded bg-wmt-tertiary" >                <p className="text-sm text-wmt-secondary" >                  <span className="font-medium">Microsoft SSO enabled</span> - Click below to sign in with your Microsoft account.
                </p>
              </div>
            )}

            {/* Submit button - changes based on auth method */}
            {authMethod !== 'denied' && (
              <>
                {authMethod === 'sso' ? (
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition bg-[#2F2F2F] text-white ${loading ? 'opacity-70' : ''}`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                      <rect width="10" height="10" fill="#F25022"/>
                      <rect x="11" width="10" height="10" fill="#7FBA00"/>
                      <rect y="11" width="10" height="10" fill="#00A4EF"/>
                      <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                    </svg>
                    {loading ? 'Redirecting to Microsoft...' : 'Sign in with Microsoft'}
                  </button>
                ) : authMethod === 'password' ? (
                  <button
                    type="submit"
                    disabled={loading}
                    className="alliance-btn-primary w-full"
                    data-testid="login-button"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={true}
                    className="alliance-btn-primary w-full opacity-50"
                  >
                    Enter your email to continue
                  </button>
                )}
              </>
            )}

            <div className="mt-4 p-3 rounded text-center bg-wmt-tertiary" >              <p className="text-xs text-wmt-secondary" >                <strong className="text-red-600">Authorized Use Only.</strong> This system is for authorized users only. 
                All activities are monitored and logged, including IP addresses, login times, 
                and user actions. Unauthorized access is prohibited and may be subject to 
                legal action.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
